import { Injectable } from '@angular/core';

import { findLast, round } from 'lodash';

import { UNIT } from '../models/types';

@Injectable({
  providedIn: 'root'
})
export class FormatService {
  connectionSpeedUnits = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  timeUnits = [
    { format: 'ns', unit: UNIT.nanoseconds },
    { format: 'μs', unit: UNIT.microseconds },
    { format: 'ms', unit: UNIT.milliseconds },
    { format: 's', unit: UNIT.seconds },
    { format: 'm', unit: UNIT.minutes },
    { format: 'h', unit: UNIT.hours },
    { format: 'd', unit: UNIT.days }
  ];
  sizeUnits = ['', 'K', 'M', 'B', 'T'];

  /**
   * Formats value and returns it without a unit attached
   * @param value
   * @param unit
   * @param fallback
   */
  // eslint-disable-next-line
  formatValue(value: number, unit: UNIT, fallback: number = 0): number {
    if (typeof value !== 'number') {
      return fallback;
    }
    switch (unit) {
      case UNIT.bitsPerSecond:
        return this._formatBitsPerSecond(value);
      case UNIT.count:
        return this._formatCount(value);
      case UNIT.nanoseconds:
      case UNIT.microseconds:
      case UNIT.milliseconds:
      case UNIT.seconds:
        return this._formatTime(value, unit);
      case UNIT.percentage:
        return this._formatPercentage(value);
    }
  }

  /**
   * Formats value based on unit
   *
   * @param value
   * @param unit
   * @param fallback {string} The returning value in case value is falsy
   */
  // eslint-disable-next-line
  formatValueWithUnit(value: number, unit: UNIT, fallback: string = `0`): string {
    if (typeof value !== 'number') {
      return fallback;
    }

    switch (unit) {
      case UNIT.bitsPerSecond:
        return this._formatBitsPerSecondWithUnit(value);
      case UNIT.count:
        return this._formatCountWithUnit(value);
      case UNIT.nanoseconds:
      case UNIT.microseconds:
      case UNIT.milliseconds:
      case UNIT.seconds:
        return this._formatTimeWithUnit(value, unit);
      case UNIT.percentage:
        return this._formatPercentageWithUnit(value);
    }
  }
  // eslint-disable-next-line
  formatBytesToBitsPerSecond(value: number, granularity: number, fallback: number = 0): number {
    if (value !== 0 && !value) {
      return fallback;
    }
    return (value * 8) / (granularity * 60);
  }

  formatNumberWithCommas(x: number): string {
    return typeof x === 'number' ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
  }
  formatMegaBites(megaBite: number, decimals: number): string {
    if (megaBite == 0) return '0Mbps';
    const k = 1000,
      dm = decimals || 2,
      sizes = ['Mbps', 'Gbps', 'Tbps', 'Pbps', 'Ebps', 'Zbps', 'Ybps'],
      i = Math.floor(Math.log(megaBite) / Math.log(k));
    return parseFloat((megaBite / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  private _formatBitsPerSecond(value: number): number {
    const i = Math.floor(Math.log(value) / Math.log(1000));
    return i < 0 ? value : round(value / Math.pow(1000, i), 2);
  }

  private _formatBitsPerSecondWithUnit(value: number): string {
    const i = Math.floor(Math.log(value) / Math.log(1000));
    if (i < 0) {
      return '< 1bps';
    }
    const result = round(value / Math.pow(1000, i), 2);
    return `${result}${this.connectionSpeedUnits[i]}`;
  }

  private _formatCount(value: number): number {
    const index = Math.floor(Math.log(value) / Math.log(1000));
    const result = round(value / Math.pow(1000, index), 2);
    return result ? result : value;
  }

  private _formatCountWithUnit(value: number): string {
    const index = Math.floor(Math.log(value) / Math.log(1000));
    const result = round(value / Math.pow(1000, index), 2);
    return !!result ? `${result}${this.sizeUnits[index]}` : `${value}`; //eslint-disable-line
  }

  private _getTimeInAllFormats(value: number, unit?: number): { value: number; unit: UNIT }[] {
    // @TODO: we need to use the existing pipe instead of this but in v1.0.2
    const microSeconds = value / 1000;
    const milliSeconds = microSeconds / 1000;
    const seconds = milliSeconds / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;

    const nanosecondsMicro = value * 1000;
    const milliSecondsMicro = value / 1000;
    const secondsMicro = milliSecondsMicro / 1000;
    const minutesMicro = secondsMicro / 60;
    const hoursMicro = minutesMicro / 60;
    const daysMicro = hoursMicro / 24;

    const microsecondsMilli = value * 1000;
    const secondsMilli = value / 1000;
    const minutesMilli = secondsMilli / 60;
    const hoursMilli = minutesMilli / 60;
    const daysMilli = hoursMilli / 24;

    const millisecondsSeconds = value * 1000;
    const microsecondsSeconds = millisecondsSeconds * 1000;
    const nanosecondsSeconds = microsecondsSeconds * 1000;
    const minutesSeconds = value / 60;
    const hoursSeconds = minutesSeconds / 60;
    const daysSeconds = hoursSeconds / 24;

    switch (unit) {
      case UNIT.nanoseconds:
        return [
          { value, unit: UNIT.nanoseconds },
          { value: microSeconds, unit: UNIT.microseconds },
          { value: milliSeconds, unit: UNIT.milliseconds },
          { value: seconds, unit: UNIT.seconds },
          { value: minutes, unit: UNIT.minutes },
          { value: hours, unit: UNIT.hours },
          { value: days, unit: UNIT.days }
        ];
      case UNIT.microseconds:
        return [
          { value: nanosecondsMicro, unit: UNIT.nanoseconds },
          { value, unit: UNIT.microseconds },
          { value: milliSecondsMicro, unit: UNIT.milliseconds },
          { value: secondsMicro, unit: UNIT.seconds },
          { value: minutesMicro, unit: UNIT.minutes },
          { value: hoursMicro, unit: UNIT.hours },
          { value: daysMicro, unit: UNIT.days }
        ];
      case UNIT.milliseconds:
        return [
          { value: microsecondsMilli, unit: UNIT.microseconds },
          { value, unit: UNIT.milliseconds },
          { value: secondsMilli, unit: UNIT.seconds },
          { value: minutesMilli, unit: UNIT.minutes },
          { value: hoursMilli, unit: UNIT.hours },
          { value: daysMilli, unit: UNIT.days }
        ];
      case UNIT.seconds:
        return [
          { value: nanosecondsSeconds, unit: UNIT.nanoseconds },
          { value: microsecondsSeconds, unit: UNIT.microseconds },
          { value: millisecondsSeconds, unit: UNIT.milliseconds },
          { value, unit: UNIT.seconds },
          { value: minutesSeconds, unit: UNIT.minutes },
          { value: hoursSeconds, unit: UNIT.hours },
          { value: daysSeconds, unit: UNIT.days }
        ];
    }
  }

  private _formatTime(value: number, unit?: number): number {
    const timeArr = this._getTimeInAllFormats(value, unit);
    const result = findLast(timeArr, item => item >= 1);
    return result ? Math.round((result + Number.EPSILON) * 100) / 100 : value;
  }

  private _formatTimeWithUnit(value: number, unit?: UNIT): string {
    const timeArr = this._getTimeInAllFormats(value, unit);
    const result = findLast(timeArr, item => item.value >= 1);
    return result
      ? `${Math.round((result.value + Number.EPSILON) * 100) / 100}${
          this.timeUnits.find(timeUnit => timeUnit.unit === result.unit).format
        }`
      : `${value}${this.timeUnits[unit].format}`;
  }

  private _formatPercentage(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private _formatPercentageWithUnit(value: number): string {
    return `${Math.round((value + Number.EPSILON) * 100) / 100}%`;
  }
}
